import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { createPublicApi } from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

const DEFAULT_SLOTS = ['08:00','08:30','09:00','09:30','10:00','10:30','11:00','11:30','12:00','12:30','13:00','13:30','14:00','14:30','17:00','17:30','18:00','18:30','19:00','19:30','20:00','20:30','21:00','21:30'];

const Reservation = () => {
  const { slug } = useParams();
  const [timeSlots, setTimeSlots]             = useState(DEFAULT_SLOTS);
  const [reservationPolicy, setReservationPolicy] = useState([
    'Reservations held for 15 minutes',
    'Groups of 8+ require a deposit',
    'Cancellations within 2 hours may incur a fee',
    'Special accommodations available on request',
  ]);
  const [maxGuests, setMaxGuests]             = useState(8);
  const [openingHours, setOpeningHours]       = useState({ mon_fri:'7:00 AM – 10:00 PM', sat:'8:00 AM – 11:00 PM', sun:'8:00 AM – 11:00 PM' });
  const [menuItems, setMenuItems]             = useState([]);
  const [menuLoading, setMenuLoading]         = useState(false);
  const [showPreOrder, setShowPreOrder]       = useState(false);
  const [preOrderCart, setPreOrderCart]       = useState({}); // {menu_item_id: quantity}
  const [gst, setGst]                         = useState({ enabled:false, rate:0, label:'GST' });

  const [form, setForm] = useState({
    name:'', phone:'', email:'', guests:2,
    reservation_date:'', reservation_time:'', special_requests:'',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess]       = useState(false);
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (!slug) return;
    const pubApi = createPublicApi(slug);
    pubApi.settings.get().then(r => {
      const d = r.data.data || {};
      if (d.time_slots) setTimeSlots(d.time_slots.split(',').filter(Boolean));
      if (d.max_guests) setMaxGuests(parseInt(d.max_guests) || 8);
      if (d.opening_hours_mon_fri || d.opening_hours_sat || d.opening_hours_sun) {
        setOpeningHours({ mon_fri:d.opening_hours_mon_fri||'7:00 AM – 10:00 PM', sat:d.opening_hours_sat||'8:00 AM – 11:00 PM', sun:d.opening_hours_sun||'8:00 AM – 11:00 PM' });
      }
      if (d.reservation_policy) setReservationPolicy(d.reservation_policy.split('||').filter(Boolean));
      setGst({ enabled:d.gst_enabled==='true', rate:parseFloat(d.gst_rate)||0, label:d.gst_label||'GST' });
    }).catch(() => {});

    // Load menu for pre-order
    setMenuLoading(true);
    pubApi.menu.getAll({ limit:100 }).then(r => setMenuItems(r.data.data || [])).catch(()=>{}).finally(()=>setMenuLoading(false));
  }, [slug]);

  // Pre-order cart helpers
  const updatePreOrder = (itemId, delta) => {
    setPreOrderCart(prev => {
      const curr = prev[itemId] || 0;
      const next = Math.max(0, curr + delta);
      if (next === 0) { const n = {...prev}; delete n[itemId]; return n; }
      return { ...prev, [itemId]: next };
    });
  };

  const preOrderItems = Object.entries(preOrderCart).map(([id, qty]) => {
    const item = menuItems.find(m => m.id === parseInt(id));
    return item ? { menu_item_id: parseInt(id), quantity: qty, item } : null;
  }).filter(Boolean);

  const preOrderSubtotal = preOrderItems.reduce((s, i) => s + parseFloat(i.item.price) * i.quantity, 0);
  const preOrderTax = gst.enabled && gst.rate > 0 ? preOrderSubtotal * gst.rate / 100 : 0;
  const preOrderTotal = preOrderSubtotal + preOrderTax;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.reservation_date || !form.reservation_time) return toast.error('Please select date and time');
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        pre_order_items: preOrderItems.map(i => ({ menu_item_id: i.menu_item_id, quantity: i.quantity })),
      };
      const pubApi = createPublicApi(slug);
      await pubApi.reservations.create(payload);
      setSuccess(true);
      toast.success('Reservation submitted!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit reservation');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <motion.div initial={{ scale:0.8, opacity:0 }} animate={{ scale:1, opacity:1 }} className="text-center max-w-md px-4">
          <div className="text-7xl mb-6">🎉</div>
          <h2 className="font-display text-4xl font-bold text-primary mb-4">Reservation Received!</h2>
          <p className="text-secondary mb-4">
            Thank you, {form.name}! Your table for {form.guests} on {form.reservation_date} at {form.reservation_time} is pending confirmation.
          </p>
          {preOrderItems.length > 0 && (
            <div style={{ background:'#fff4ee', border:'1px solid #f0d0b0', borderRadius:14, padding:'14px 18px', marginBottom:16, textAlign:'left' }}>
              <p style={{ color:'#c96428', fontWeight:700, fontSize:14, marginBottom:8 }}>🍽️ Your Pre-order:</p>
              {preOrderItems.map(i => (
                <p key={i.menu_item_id} style={{ color:'#3d2010', fontSize:13, margin:'3px 0' }}>• {i.item.name} × {i.quantity}</p>
              ))}
              <p style={{ color:'#c96428', fontWeight:700, marginTop:8 }}>Total: ₹{preOrderTotal.toFixed(0)}</p>
            </div>
          )}
          <p className="text-secondary mb-8 text-sm">We'll contact you shortly to confirm.</p>
          <button onClick={() => {
            setSuccess(false);
            setForm({ name:'', phone:'', email:'', guests:2, reservation_date:'', reservation_time:'', special_requests:'' });
            setPreOrderCart({});
          }} className="btn-primary">
            Make Another Reservation
          </button>
        </motion.div>
      </div>
    );
  }

  // Group menu by category
  const categories = [...new Set(menuItems.map(m => m.category_name || 'Other'))];

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero */}
      <div className="relative bg-bark-900 py-20 mb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage:'url(https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1400&q=80)', backgroundSize:'cover', backgroundPosition:'center' }}/>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} className="font-display text-5xl font-bold text-cream-100 mb-4">
            Reserve Your Table
          </motion.h1>
          <p className="text-bark-400 text-lg max-w-xl mx-auto">
            Book ahead and optionally pre-order your food for a seamless dining experience.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

          {/* Info Panel */}
          <div className="lg:col-span-2 space-y-6">
            <div className="card p-6">
              <h3 className="font-display text-xl font-bold text-primary mb-4">Opening Hours</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-bark-500">Mon – Fri</span><span className="font-medium">{openingHours.mon_fri}</span></div>
                <div className="flex justify-between"><span className="text-bark-500">Saturday</span><span className="font-medium">{openingHours.sat}</span></div>
                <div className="flex justify-between"><span className="text-bark-500">Sunday</span><span className="font-medium">{openingHours.sun}</span></div>
              </div>
            </div>
            <div className="card p-6">
              <h3 className="font-display text-xl font-bold text-primary mb-4">Reservation Policy</h3>
              <ul className="space-y-2 text-sm text-bark-600">
                {reservationPolicy.map((pt,i)=><li key={i}>• {pt}</li>)}
              </ul>
            </div>

            {/* Pre-order summary if items selected */}
            {preOrderItems.length > 0 && (
              <div style={{ background:'#fff4ee', border:'1px solid #f0d0b0', borderRadius:16, padding:'18px' }}>
                <p style={{ color:'#c96428', fontWeight:700, fontSize:15, marginBottom:12 }}>🍽️ Pre-order Summary</p>
                {preOrderItems.map(i => (
                  <div key={i.menu_item_id} style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'#3d2010', padding:'4px 0', borderBottom:'1px solid #f0d0b0' }}>
                    <span>{i.item.name} × {i.quantity}</span>
                    <span style={{ color:'#c96428', fontWeight:700 }}>₹{(parseFloat(i.item.price)*i.quantity).toFixed(0)}</span>
                  </div>
                ))}
                {gst.enabled && gst.rate > 0 && (
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#8b6347', padding:'6px 0' }}>
                    <span>{gst.label} ({gst.rate}%)</span>
                    <span>₹{preOrderTax.toFixed(0)}</span>
                  </div>
                )}
                <div style={{ display:'flex', justifyContent:'space-between', fontWeight:800, fontSize:15, color:'#c96428', paddingTop:8, borderTop:'2px solid #c96428', marginTop:4 }}>
                  <span>Total</span>
                  <span>₹{preOrderTotal.toFixed(0)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Form */}
          <div className="lg:col-span-3 card p-8">
            <h2 className="font-display text-2xl font-bold text-primary mb-6">Book Your Table</h2>
            <form onSubmit={handleSubmit} className="space-y-4">

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-bark-700 mb-1">Full Name *</label>
                  <input className="input-field" placeholder="Jane Doe" required
                    value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}/>
                </div>
                <div>
                  <label className="block text-sm font-medium text-bark-700 mb-1">Phone *</label>
                  <input className="input-field" placeholder="+91 98765 43210" required
                    value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))}/>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-bark-700 mb-1">Email</label>
                <input type="email" className="input-field" placeholder="jane@example.com"
                  value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))}/>
              </div>

              <div>
                <label className="block text-sm font-medium text-bark-700 mb-2">Number of Guests *</label>
                <div className="flex gap-2 flex-wrap">
                  {Array.from({length:maxGuests},(_,i)=>i+1).map(n=>(
                    <button key={n} type="button" onClick={()=>setForm(f=>({...f,guests:n}))}
                      className={`w-10 h-10 rounded-full border-2 text-sm font-bold transition-all ${form.guests===n?'bg-espresso-500 border-espresso-500 text-white':'border-bark-200 hover:border-espresso-400'}`}>
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-bark-700 mb-1">Date *</label>
                <input type="date" className="input-field" min={today} required
                  value={form.reservation_date} onChange={e=>setForm(f=>({...f,reservation_date:e.target.value}))}/>
              </div>

              <div>
                <label className="block text-sm font-medium text-bark-700 mb-2">Time *</label>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {timeSlots.map(slot=>(
                    <button key={slot} type="button" onClick={()=>setForm(f=>({...f,reservation_time:slot}))}
                      className={`py-2 px-1 rounded-lg text-xs font-medium border transition-all ${form.reservation_time===slot?'bg-espresso-500 border-espresso-500 text-white':'border-bark-200 hover:border-espresso-400 text-bark-600'}`}>
                      {slot}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-bark-700 mb-1">Special Requests</label>
                <textarea className="input-field h-20 resize-none" placeholder="Allergies, dietary requirements, special occasions..."
                  value={form.special_requests} onChange={e=>setForm(f=>({...f,special_requests:e.target.value}))}/>
              </div>

              {/* Pre-Order Toggle */}
              <div style={{ border:'2px solid #f0d0b0', borderRadius:16, overflow:'hidden' }}>
                <button type="button" onClick={()=>setShowPreOrder(!showPreOrder)}
                  style={{
                    width:'100%', padding:'14px 20px', background: showPreOrder ? '#fff4ee' : 'white',
                    border:'none', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center',
                    fontFamily:'inherit',
                  }}>
                  <div style={{ textAlign:'left' }}>
                    <p style={{ fontWeight:700, color:'#c96428', fontSize:14, margin:0 }}>
                      🍽️ Pre-order Food (Optional)
                    </p>
                    <p style={{ color:'#8b6347', fontSize:12, margin:'2px 0 0' }}>
                      {preOrderItems.length > 0 ? `${preOrderItems.length} item(s) · ₹${preOrderTotal.toFixed(0)}` : 'Order your food in advance — skip the wait!'}
                    </p>
                  </div>
                  <span style={{ fontSize:18, color:'#c96428', transition:'transform 0.2s', transform: showPreOrder ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                </button>

                <AnimatePresence>
                  {showPreOrder && (
                    <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}}
                      style={{ overflow:'hidden', borderTop:'1px solid #f0d0b0' }}>
                      <div style={{ padding:'16px 20px', maxHeight:400, overflowY:'auto' }}>
                        {menuLoading ? (
                          <p style={{ color:'#8b6347', textAlign:'center', padding:'20px 0' }}>Loading menu...</p>
                        ) : categories.map(cat => {
                          const catItems = menuItems.filter(m => (m.category_name||'Other') === cat && m.is_available);
                          if (catItems.length === 0) return null;
                          return (
                            <div key={cat} style={{ marginBottom:16 }}>
                              <p style={{ color:'#8b6347', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:8 }}>{cat}</p>
                              {catItems.map(item => {
                                const qty = preOrderCart[item.id] || 0;
                                return (
                                  <div key={item.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid #fdf0e6' }}>
                                    <div style={{ flex:1 }}>
                                      <p style={{ fontWeight:600, color:'#1a0f00', fontSize:13, margin:0 }}>{item.name}</p>
                                      <p style={{ color:'#c96428', fontWeight:700, fontSize:12, margin:'2px 0 0' }}>₹{parseFloat(item.price).toFixed(0)}</p>
                                    </div>
                                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                      {qty > 0 ? (
                                        <>
                                          <button type="button" onClick={()=>updatePreOrder(item.id,-1)}
                                            style={{ width:28,height:28,borderRadius:'50%',border:'1.5px solid #f0d0b0',background:'white',color:'#c96428',fontWeight:800,fontSize:16,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center' }}>−</button>
                                          <span style={{ fontWeight:800,color:'#1a0f00',minWidth:16,textAlign:'center',fontSize:14 }}>{qty}</span>
                                          <button type="button" onClick={()=>updatePreOrder(item.id,1)}
                                            style={{ width:28,height:28,borderRadius:'50%',border:'none',background:'linear-gradient(135deg,#c96428,#9a4a1e)',color:'white',fontWeight:800,fontSize:16,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center' }}>+</button>
                                        </>
                                      ) : (
                                        <button type="button" onClick={()=>updatePreOrder(item.id,1)}
                                          style={{ padding:'6px 14px',borderRadius:8,border:'1.5px solid #f0d0b0',background:'#fff4ee',color:'#c96428',fontWeight:700,fontSize:12,cursor:'pointer',fontFamily:'inherit' }}>
                                          Add
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <motion.button whileTap={{scale:0.98}} type="submit" disabled={submitting}
                className="btn-primary w-full py-4 text-base disabled:opacity-60">
                {submitting ? 'Submitting...' : `Reserve Table for ${form.guests} Guest${form.guests!==1?'s':''}${preOrderItems.length>0?' + Pre-order':''}`}
              </motion.button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reservation;
